import Star from '@mui/icons-material/Star'
import {circularProgressClasses, IconButton, svgIconClasses, Tooltip} from '@mui/material'
import {type ChangeEvent, memo, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import type {SpotItem} from '../../../../@types/entity/Spot'
import type {VisitPartial} from '../../../../@types/entity/Visit'
import {AsyncIconButton} from '../../../../components/Form/AsyncIconButton'
import {
  useVisitCollection,
  useVisitCreate,
  useVisitFilterFromCurrentUser,
  useVisitUpdate,
} from '../../../../hooks/entity/useVisit'
import {useAsyncSubmit} from '../../../../hooks/useAsyncSubmit'
import {useEffectEvent} from '../../../../hooks/useEffectEvent'
import {useIsAllowed} from '../../../../hooks/useSignin'
import {withAsyncContext} from '../../../../states/AsyncContext'
import {useLogged} from '../../../../states/auth'

interface Props {
  spot: SpotItem
  showAdmin?: boolean
}

export const VisitStarButton = withAsyncContext(memo(({spot, showAdmin}: Props) => {
  const spotIri = spot['@id']
  const logged = useLogged()
  const {t} = useTranslation()
  const {visits, error} = useVisitCollection({...useVisitFilterFromCurrentUser()}, undefined, !spot.demo)
  const visitCreate = useVisitCreate()
  const visitUpdate = useVisitUpdate()
  const isAllowed = useIsAllowed(spotIri)

  const visit = useMemo(() => {
    if (!visits) {
      return undefined
    }
    return visits.member.find((visit: VisitPartial) => visit.spot === spotIri)
  }, [spotIri, visits])

  const onSubmit = useAsyncSubmit(async () => {
    if (!visit) {
      await visitCreate({spot: spotIri, stared: true})
    } else {
      await visitUpdate(visit, {stared: !visit.stared})
    }
  })

  const handleClick = useEffectEvent(async (event: ChangeEvent<HTMLButtonElement>) => {
    event.preventDefault()
    await onSubmit()
  })

  if (!logged || error || !spot || spot.demo) {
    return null
  }

  if (!visits) {
    return null
  }

  const isAdmin = isAllowed('admin')
  if (isAdmin === undefined) {
    return null
  }

  if (isAdmin) {
    if (showAdmin === false) {
      return null
    }

    return <Tooltip title={t('common.menu.spot.unstar_admin')}>
      {/* biome-ignore lint/a11y/useFocusableInteractive: wrapper for disabled button */}
      <div role="button">
        <IconButton
          size="small"
          disabled
          sx={{
            [`.${svgIconClasses.root}`]: {
              fontSize: 16,
            },
          }}
        >
          <Star/>
        </IconButton>
      </div>
    </Tooltip>
  }

  return <Tooltip title={t(`common.menu.spot.${visit?.stared ? 'unstar' : 'star'}`)}>
    <AsyncIconButton
      size="small"
      // @ts-ignore
      onClick={handleClick}
      successTtl={0}
      color={visit?.stared ? 'inherit' : 'secondary'}
      sx={{
        [`.${svgIconClasses.root}`]: {
          fontSize: 16,
        },
        [`.${circularProgressClasses.root}`]: {
          width: '16px !important',
          height: '16px !important',
        },
      }}
    >
      <Star/>
    </AsyncIconButton>
  </Tooltip>
}))
